import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../lib/api";
import { cn } from "../../lib/cn";

type Appointment = {
  id: string;
  branch_name?: string | null;
  customer_name?: string | null;
  service_name?: string | null;
  staff_name?: string | null;
  start_at: string;
  end_at?: string | null;
  status: string;
  allowed_actions: string[];
};

type CalendarData = {
  items: Appointment[];
  meta: { page: number; size: number; total: number };
};

const STATUS_COLORS: Record<string, string> = {
  pending_confirmation: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-gray-100 text-gray-500",
  no_show: "bg-red-100 text-red-700",
};

const STATUS_LABELS: Record<string, string> = {
  pending_confirmation: "Pending",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

function isoDate(d: Date) {
  return d.toISOString().split("T")[0];
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatHourBucket(iso: string) {
  const d = new Date(iso);
  d.setMinutes(0, 0, 0);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function buildDateStrip(anchor: Date): Date[] {
  const days: Date[] = [];
  for (let i = -3; i <= 3; i += 1) {
    const d = new Date(anchor);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

export function TgCalendarPage() {
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState<Date>(new Date());
  const [stripAnchor, setStripAnchor] = useState<Date>(new Date());
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [serviceFilter, setServiceFilter] = useState<string>("");
  const [staffFilter, setStaffFilter] = useState<string>("");

  const dayStr = isoDate(selectedDay);
  const { data, isLoading } = useQuery<CalendarData>({
    queryKey: ["tg-calendar", dayStr, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({ day: dayStr, size: "100" });
      if (statusFilter) params.set("status", statusFilter);
      return apiGet(`/v3/tg/calendar?${params}`);
    },
  });

  const strip = buildDateStrip(stripAnchor);
  const items = useMemo(() => [...(data?.items ?? [])].sort((a, b) => a.start_at.localeCompare(b.start_at)), [data?.items]);
  const serviceOptions = useMemo(
    () => [...new Set(items.map((x) => x.service_name).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b)),
    [items]
  );
  const staffOptions = useMemo(
    () => [...new Set(items.map((x) => x.staff_name).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b)),
    [items]
  );
  const visibleItems = useMemo(() => {
    return items.filter((x) => {
      if (serviceFilter && (x.service_name || "") !== serviceFilter) return false;
      if (staffFilter && (x.staff_name || "") !== staffFilter) return false;
      return true;
    });
  }, [items, serviceFilter, staffFilter]);
  const groupedAgenda = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    visibleItems.forEach((appt) => {
      const key = formatHourBucket(appt.start_at);
      map.set(key, [...(map.get(key) ?? []), appt]);
    });
    return [...map.entries()].map(([label, rows]) => ({ label, rows }));
  }, [visibleItems]);
  const summary = useMemo(() => {
    const counts = { total: visibleItems.length, pending: 0, confirmed: 0, completed: 0 };
    for (const appt of visibleItems) {
      if (appt.status === "pending_confirmation") counts.pending += 1;
      if (appt.status === "confirmed") counts.confirmed += 1;
      if (appt.status === "completed") counts.completed += 1;
    }
    return counts;
  }, [visibleItems]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Calendar</h1>
          <p className="text-xs text-gray-500">Quick day agenda</p>
        </div>
        <button
          type="button"
          onClick={() => {
            const today = new Date();
            setSelectedDay(today);
            setStripAnchor(today);
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700"
        >
          Today
        </button>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setStripAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })}
          className="flex-shrink-0 px-2 py-2 text-lg font-bold leading-none text-gray-500"
          aria-label="Previous week"
        >
          &lt;
        </button>
        <div className="flex flex-1 gap-1.5 overflow-x-auto pb-1">
          {strip.map((d) => {
            const key = isoDate(d);
            const active = key === dayStr;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setSelectedDay(d); setStripAnchor(d); }}
                className={cn(
                  "flex flex-shrink-0 flex-col items-center rounded-xl px-3 py-1.5 text-xs transition-colors",
                  active ? "bg-teal-600 text-white" : "border border-gray-200 bg-white text-gray-600"
                )}
              >
                <span>{d.toLocaleDateString([], { weekday: "short" })}</span>
                <span className="font-bold">{d.getDate()}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setStripAnchor((d) => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })}
          className="flex-shrink-0 px-2 py-2 text-lg font-bold leading-none text-gray-500"
          aria-label="Next week"
        >
          &gt;
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Appointments</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{summary.total}</p>
          <p className="text-xs text-gray-500">{dayStr}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">Filters</p>
          <p className="mt-1 truncate text-sm font-semibold text-gray-900">{serviceFilter || "All services"}</p>
          <p className="truncate text-xs text-gray-500">{staffFilter || "All staff"}</p>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <div className="flex-shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700">
          Pending: <span className="font-semibold">{summary.pending}</span>
        </div>
        <div className="flex-shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700">
          Confirmed: <span className="font-semibold">{summary.confirmed}</span>
        </div>
        <div className="flex-shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700">
          Done: <span className="font-semibold">{summary.completed}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {["", "pending_confirmation", "confirmed", "completed", "cancelled"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              statusFilter === s ? "border-teal-600 bg-teal-600 text-white" : "border-gray-300 bg-white text-gray-600"
            )}
          >
            {s ? (STATUS_LABELS[s] ?? s) : "All"}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <div>
          <p className="mb-1 text-xs text-gray-500">Service</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setServiceFilter("")}
              className={cn(
                "flex-shrink-0 rounded-full border px-3 py-1.5 text-xs",
                serviceFilter === "" ? "border-teal-600 bg-teal-600 text-white" : "border-gray-300 bg-white text-gray-700"
              )}
            >
              All services
            </button>
            {serviceOptions.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setServiceFilter((v) => (v === name ? "" : name))}
                className={cn(
                  "flex-shrink-0 rounded-full border px-3 py-1.5 text-xs",
                  serviceFilter === name ? "border-teal-600 bg-teal-600 text-white" : "border-gray-300 bg-white text-gray-700"
                )}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-1 text-xs text-gray-500">Specialist</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setStaffFilter("")}
              className={cn(
                "flex-shrink-0 rounded-full border px-3 py-1.5 text-xs",
                staffFilter === "" ? "border-teal-600 bg-teal-600 text-white" : "border-gray-300 bg-white text-gray-700"
              )}
            >
              All staff
            </button>
            {staffOptions.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setStaffFilter((v) => (v === name ? "" : name))}
                className={cn(
                  "flex-shrink-0 rounded-full border px-3 py-1.5 text-xs",
                  staffFilter === name ? "border-teal-600 bg-teal-600 text-white" : "border-gray-300 bg-white text-gray-700"
                )}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? <div className="py-6 text-center text-gray-400">Loading...</div> : null}

      {!isLoading && visibleItems.length === 0 ? (
        <div className="py-12 text-center text-gray-400">No appointments for this day.</div>
      ) : null}

      <div className="space-y-3">
        {groupedAgenda.map((group) => (
          <section key={group.label} className="space-y-2">
            <div className="rounded-lg bg-gray-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
              {group.label}
            </div>
            {group.rows.map((appt) => (
              <button
                key={appt.id}
                type="button"
                className="flex w-full items-center justify-between rounded-xl border border-gray-100 bg-white p-3 text-left shadow-sm"
                onClick={() => navigate(`/tg/appointments/${appt.id}`)}
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900">{appt.customer_name ?? "Unknown"}</p>
                  <p className="truncate text-xs text-gray-500">{appt.service_name || "Service"} • {appt.staff_name || "Staff"}</p>
                  <p className="text-xs font-semibold text-gray-600">
                    {formatTime(appt.start_at)}
                    {appt.end_at ? ` - ${formatTime(appt.end_at)}` : ""}
                  </p>
                  {appt.branch_name ? <p className="truncate text-[11px] text-gray-400">{appt.branch_name}</p> : null}
                </div>
                <span className={cn("ml-2 flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium", STATUS_COLORS[appt.status] ?? "bg-gray-100 text-gray-600")}>
                  {STATUS_LABELS[appt.status] ?? appt.status}
                </span>
              </button>
            ))}
          </section>
        ))}
      </div>

      {data && data.meta.total > data.items.length ? (
        <p className="text-center text-xs text-gray-400">
          Showing {data.items.length} of {data.meta.total}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => navigate(`/tg/book?day=${dayStr}`)}
        className="fixed bottom-20 right-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-teal-600 text-2xl text-white shadow-lg"
        aria-label="New booking"
      >
        +
      </button>
    </div>
  );
}
